import React from 'react'
import { useTranslation } from 'react-i18next'
import { Award, AlertTriangle, Check, Clock, Info, RotateCcw, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { TemplateThumb } from '@/components/features/certificates/TemplateThumb'
import { useLocale } from '@/lib/LocaleContext'
import { sdk } from '@/lib/sdk'
import { cn } from '@/lib/utils'
import type { CertificateTemplate } from '@/types/certificates'
import {
  CERTIFICATE_TEXT_MAX, CERTIFICATE_VARIABLES, fillCertificateText, formatHours, formatHoursWithUnit,
  type CertificateDraft,
} from './certificateText'

interface TemplatePickerProps {
  selectedId: string | null
  onSelect: (template: CertificateTemplate) => void
  onResolved: (template: CertificateTemplate | null) => void
  disabled?: boolean
  error?: string
}

/** The community's active templates, the platform default first. Same card and thumbnail as the Templates tab. */
export const TemplatePicker = ({ selectedId, onSelect, onResolved, disabled, error }: TemplatePickerProps) => {
  const { t } = useTranslation('courses')
  const [query, setQuery] = React.useState('')
  const [debounced, setDebounced] = React.useState('')
  const [templates, setTemplates] = React.useState<CertificateTemplate[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [failed, setFailed] = React.useState(false)
  const [attempt, setAttempt] = React.useState(0)
  const onResolvedRef = React.useRef(onResolved)
  onResolvedRef.current = onResolved
  const selectedRef = React.useRef(selectedId)
  selectedRef.current = selectedId

  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query), 300)
    return () => window.clearTimeout(timer)
  }, [query])

  React.useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setFailed(false)
    sdk.certificates.listCommunityTemplates(debounced)
      .then((list) => {
        if (cancelled) return
        // Only active ones can be chosen, and the default always leads.
        const active = list.filter((item) => item.status === 'ACTIVE')
        active.sort((a, b) => Number(Boolean(b.isSystemDefault)) - Number(Boolean(a.isSystemDefault)))
        setTemplates(active)
        const current = selectedRef.current
        onResolvedRef.current(active.find((item) => (current ? item.id === current : item.isSystemDefault)) ?? null)
      })
      .catch(() => { if (!cancelled) setFailed(true) })
      .finally(() => { if (!cancelled) setIsLoading(false) })
    return () => { cancelled = true }
  }, [debounced, attempt])

  const spaces = new Set(templates.map((item) => item.space?.id).filter(Boolean))
  const showSpace = spaces.size > 1

  return (
    <div className="min-w-0 space-y-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('certificateForm.search')}
          aria-label={t('certificateForm.search')}
          className="pl-9"
          disabled={disabled}
          maxLength={100}
        />
      </div>

      {isLoading && templates.length === 0 ? (
        <div className="grid gap-3 sm:grid-cols-2" aria-busy="true">
          {[1, 2].map((i) => (
            <div key={i} className="overflow-hidden rounded-xl border border-gray-200">
              <Skeleton className="aspect-[1.414/1] w-full rounded-none" />
              <div className="space-y-2 p-3"><Skeleton className="h-4 w-2/3" /><Skeleton className="h-3 w-1/2" /></div>
            </div>
          ))}
        </div>
      ) : failed ? (
        <div role="alert" className="rounded-lg bg-red-50 p-4 text-center text-sm text-red-700">
          <AlertTriangle className="mx-auto mb-2 h-6 w-6" />
          <p>{t('certificateForm.templatesError')}</p>
          <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => setAttempt((n) => n + 1)}>
            {t('retry')}
          </Button>
        </div>
      ) : templates.length === 0 ? (
        <p className="rounded-lg bg-gray-50 p-4 text-center text-sm text-gray-500">{t('certificateForm.noMatch')}</p>
      ) : (
        <div role="radiogroup" aria-label={t('certificateForm.templateLabel')} className={cn('grid gap-3 sm:grid-cols-2', isLoading && 'opacity-60')}>
          {templates.map((template) => {
            const selected = template.isSystemDefault ? selectedId === null || selectedId === template.id : selectedId === template.id
            return (
              <button
                key={template.id}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={disabled}
                data-template-id={template.id}
                onClick={() => onSelect(template)}
                className={cn(
                  'group flex min-w-0 flex-col overflow-hidden rounded-xl border-2 bg-white text-left transition-all',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:ring-offset-1',
                  selected ? 'border-primary shadow-sm' : 'border-gray-200 hover:border-gray-300',
                  disabled && 'cursor-not-allowed opacity-50'
                )}
              >
                <div className="relative aspect-[1.414/1] w-full overflow-hidden bg-gray-100">
                  <TemplateThumb template={template} />
                  {selected && (
                    <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white">
                      <Check className="h-4 w-4" />
                    </span>
                  )}
                </div>
                <div className="min-w-0 space-y-1 p-3">
                  <h4 className="line-clamp-2 text-sm font-semibold text-gray-900">{template.name}</h4>
                  <p className="line-clamp-1 text-xs text-gray-500">{template.title}</p>
                  <p className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Award className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">
                      {showSpace && template.space ? `${template.space.name} · ` : ''}
                      {template.isSystemDefault ? t('certificateForm.defaultBadge') : t(`certificateForm.kind.${template.kind}`)}
                    </span>
                  </p>
                </div>
              </button>
            )
          })}
        </div>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  )
}

export interface CourseCertificateFieldsProps {
  draft: CertificateDraft
  onChange: (draft: CertificateDraft) => void
  courseTitle: string
  /** The server's default text on edit; a translated copy of it while the course does not exist yet. */
  defaultText: string
  /** Hours from the lesson durations. `null`: some lesson has none; `undefined`: the course has no lessons yet. */
  workloadHours: number | null | undefined
  /** Titles of the lessons without a duration; `null` when the course has no lessons to list. */
  lessonsWithoutDuration: string[] | null
  /** The saved template was archived or deleted: nothing is issued until another is chosen. */
  templateInactive?: boolean
  /** The platform default is archived or deleted: warns, but saving stays allowed. */
  defaultInactive?: boolean
  /** The course is being read (edit): the setting is not known yet. */
  isLoading?: boolean
  loadFailed?: boolean
  onRetry?: () => void
  errors?: { templateId?: string; text?: string }
  disabled?: boolean
}

/**
 * "Deseja emitir certificado?": the switch, the template picker, the text with its preview
 * and the workload. One component for the course form and the one-course step of the wizard.
 */
export const CourseCertificateFields = ({
  draft, onChange, courseTitle, defaultText, workloadHours, lessonsWithoutDuration,
  templateInactive = false, defaultInactive = false, isLoading = false, loadFailed = false, onRetry, errors = {}, disabled = false,
}: CourseCertificateFieldsProps) => {
  const { t } = useTranslation('courses')
  const { currentLocale } = useLocale()
  const [selected, setSelected] = React.useState<CertificateTemplate | null>(null)
  const textRef = React.useRef<HTMLTextAreaElement>(null)

  const text = draft.text ?? defaultText
  const templateHours = selected?.workloadHours ?? null
  const hours = typeof workloadHours === 'number' && workloadHours > 0 ? workloadHours : templateHours && templateHours > 0 ? templateHours : null
  const preview = fillCertificateText(text, {
    name: t('certificateForm.previewName'),
    subject: courseTitle.trim() || t('certificateForm.previewCourse'),
    workload: hours ? formatHoursWithUnit(hours, currentLocale, { one: t('certificateForm.hoursOne'), other: t('certificateForm.hoursOther') }) : '',
  })

  const patch = (next: Partial<CertificateDraft>) => onChange({ ...draft, ...next })

  const insertVariable = (variable: string) => {
    const el = textRef.current
    const start = el?.selectionStart ?? text.length
    const end = el?.selectionEnd ?? text.length
    const next = `${text.slice(0, start)}${variable}${text.slice(end)}`
    if (next.length > CERTIFICATE_TEXT_MAX) return
    patch({ text: next })
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(start + variable.length, start + variable.length) })
  }

  const missing = lessonsWithoutDuration ?? []

  return (
    <div className="min-w-0 space-y-4" data-testid="course-certificate">
      <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
            <Award className="h-4 w-4 flex-shrink-0 text-gray-400" />
            {t('certificateForm.question')}
          </p>
          <p className="mt-0.5 text-xs text-gray-500">{t('certificateForm.questionHelp')}</p>
        </div>
        <Switch
          checked={draft.enabled}
          disabled={disabled || isLoading}
          onCheckedChange={(enabled) => patch({ enabled })}
          aria-label={t('certificateForm.question')}
        />
      </div>

      {draft.enabled && isLoading && (
        <div className="space-y-3" aria-busy="true">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {draft.enabled && loadFailed && (
        <div role="alert" className="rounded-lg bg-red-50 p-4 text-center text-sm text-red-700">
          <p>{t('certificateForm.loadError')}</p>
          {onRetry && <Button type="button" variant="outline" size="sm" className="mt-3" onClick={onRetry}>{t('retry')}</Button>}
        </div>
      )}

      {draft.enabled && !isLoading && !loadFailed && (
        <div className="min-w-0 space-y-5">
          {defaultInactive && !templateInactive && (
            <div role="alert" className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <p className="text-xs">{t('certificateForm.defaultInactive')}</p>
            </div>
          )}
          {templateInactive && (
            <div role="alert" className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>
                <p className="font-semibold">{t('certificateForm.inactiveTitle')}</p>
                <p className="mt-0.5 text-xs">{t('certificateForm.inactiveBody')}</p>
              </div>
            </div>
          )}

          <div className="grid gap-2">
            <label className="text-sm font-medium">{t('certificateForm.templateLabel')}</label>
            <TemplatePicker
              selectedId={draft.templateId}
              disabled={disabled}
              error={errors.templateId}
              onResolved={setSelected}
              onSelect={(template) => {
                setSelected(template)
                patch({ templateId: template.isSystemDefault ? null : template.id })
              }}
            />
            <p className="text-xs text-gray-500">{t('certificateForm.templateHelp')}</p>
          </div>

          <div className="grid gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label htmlFor="certificate-text" className="text-sm font-medium">{t('certificateForm.textLabel')}</label>
              {draft.text !== null && draft.text !== defaultText && (
                <button type="button" onClick={() => patch({ text: null })} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                  <RotateCcw className="h-3 w-3" />
                  {t('certificateForm.textRestore')}
                </button>
              )}
            </div>
            <Textarea
              id="certificate-text"
              ref={textRef}
              rows={3}
              value={text}
              maxLength={CERTIFICATE_TEXT_MAX}
              disabled={disabled}
              aria-invalid={Boolean(errors.text)}
              onChange={(e) => patch({ text: e.target.value })}
            />
            {errors.text && <p className="text-xs text-red-600">{errors.text}</p>}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500">{t('certificateForm.variables')}</span>
              {CERTIFICATE_VARIABLES.map((variable) => (
                <button
                  key={variable}
                  type="button"
                  disabled={disabled}
                  onClick={() => insertVariable(variable)}
                  className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700 hover:bg-gray-200"
                >
                  {variable}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500">{t('certificateForm.variablesHelp')}</p>
          </div>

          <div className="grid gap-2">
            <span className="text-sm font-medium">{t('certificateForm.previewLabel')}</span>
            <div className="min-w-0 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center" data-testid="certificate-preview">
              <p className="text-xs text-gray-500">{t('certificateForm.previewAwarded')}</p>
              <p className="mt-1 font-serif text-lg font-semibold text-gray-900">{t('certificateForm.previewName')}</p>
              <p className="mt-1 break-words text-sm text-gray-700">{preview}</p>
            </div>
            {!hours && (
              <p className="text-xs text-gray-500">
                {workloadHours === undefined ? t('certificateForm.workloadWhenLessons') : t('certificateForm.previewNoHours')}
              </p>
            )}
          </div>

          <div className="grid gap-2 rounded-lg bg-gray-50 p-3" data-testid="certificate-workload">
            <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
              <Clock className="h-4 w-4 text-gray-400" />
              {t('certificateForm.workloadTitle')}
            </p>
            {workloadHours === undefined ? (
              <p className="text-xs text-gray-600">{t('certificateForm.workloadWhenLessons')}</p>
            ) : workloadHours === null ? (
              <p className="flex items-start gap-2 text-xs text-amber-800">
                <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                {t('certificateForm.workloadUnavailable')}
              </p>
            ) : (
              <p className="text-xs text-gray-700">{t('certificateForm.workloadCalculated', { hours: formatHours(workloadHours, currentLocale) })}</p>
            )}
            {selected && selected.workloadHours ? (
              <p className="text-xs text-gray-600">
                {t('certificateForm.workloadTemplate', { hours: formatHours(selected.workloadHours, currentLocale) })}
              </p>
            ) : null}
            {missing.length > 0 && (
              <div className="text-xs text-gray-600">
                <p className="font-medium text-gray-700">{t('certificateForm.lessonsMissing', { count: missing.length })}</p>
                <ul className="mt-1 list-inside list-disc space-y-0.5">
                  {missing.slice(0, 5).map((title, index) => <li key={`${title}-${index}`} className="break-words">{title}</li>)}
                </ul>
                {missing.length > 5 && <p className="mt-1">{t('certificateForm.lessonsMissingMore', { count: missing.length - 5 })}</p>}
              </div>
            )}
            <p className="text-xs text-gray-500">{t('certificateForm.workloadRule')}</p>
          </div>
        </div>
      )}
    </div>
  )
}
